import memoizeOne from "memoize-one";
import * as React from "react";
import * as ReactDOM from "react-dom";
import ReactMarkdown from "react-markdown";
import { tooltip } from "../../controls/api";
import FlexLayout from "../../controls/FlexLayout";
import Icon from "../../controls/Icon";
import { MainContext } from "../../views/api";
import { getOverlayComponent } from "./index";
const BORDER = 8;
const pxRE = /px$/;
function parsePos(inPos) {
    return parseInt(inPos.replace(pxRE, ""), 10);
}
function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
}
function getContainerImpl() {
    return document.getElementById("overlays");
}
const getContainer = memoizeOne(() => getContainerImpl());
function InstructionsOverlay(props) {
    const { t, overlay, overlayId } = props;
    const context = React.useContext(MainContext);
    const [open, setOpen] = React.useState(true);
    const ref = React.useRef(null);
    const abortDrag = React.useRef(null);
    const container = getContainer();
    const [pos, setPosImpl] = React.useState(() => overlay.position !== undefined
        ? overlay.position
        : { x: container.clientWidth - 100, y: container.clientHeight - 100 });
    const dragOffset = React.useRef({ x: 0, y: 0 });
    React.useEffect(() => {
        if (ref.current !== undefined) {
            // fix default position as soon as the image has been rendered once
            applyPos({ ...pos });
        }
    }, [overlay, ref.current]);
    const toggle = React.useCallback(() => {
        if (!overlay?.options?.disableCollapse) {
            setOpen((old) => !old);
        }
    }, [setOpen]);
    const applyPos = React.useCallback((posIn) => {
        if (ref.current !== null) {
            posIn.x = clamp(posIn.x, BORDER, container.clientWidth - ref.current.clientWidth - BORDER);
            posIn.y = clamp(posIn.y, BORDER, container.clientHeight - ref.current.clientHeight - BORDER);
        }
        // react may not update the style if it doesn't know the dom was manipulated directly
        ref.current.style.left = `${posIn.x}px`;
        ref.current.style.top = `${posIn.y}px`;
        setPosImpl(posIn);
    }, [setPosImpl]);
    const trackMouse = React.useCallback((evt) => {
        evt.preventDefault();
        if (evt.buttons === 0) {
            // missed the mouseup event? Maybe window was robbed of focus
            evt.currentTarget.dispatchEvent(new MouseEvent("mouseup"));
        }
        else {
            if (ref.current !== null) {
                ref.current.style.left = `${evt.pageX - dragOffset.current.x}px`;
                ref.current.style.top = `${evt.pageY - dragOffset.current.y}px`;
            }
        }
    }, [ref.current]);
    const endDrag = React.useCallback((evt) => {
        abortDrag.current?.abort();
        abortDrag.current = null;
        const { left, top } = ref.current.style;
        applyPos({ x: parsePos(left), y: parsePos(top) });
        container.style.pointerEvents = "none";
        evt.stopPropagation();
    }, [applyPos]);
    const startDrag = React.useCallback((evt) => {
        if (abortDrag.current !== null) {
            // something went wrong, already dragging?
            abortDrag.current.abort();
        }
        if (ref.current !== null) {
            dragOffset.current = {
                x: evt.pageX - ref.current.offsetLeft,
                y: evt.pageY - ref.current.offsetTop,
            };
        }
        container.style.pointerEvents = "initial";
        // the mousemove event is aborted with this controller, mouseup event is canceled
        // automatically when it's triggered.
        // This is necessary because updatePos and endDrag are hook callbacks that may get updated
        // and then removeEventListener wouldn't find the correct function to remove
        abortDrag.current = new AbortController();
        // capture makes it so that the container receives the event, not the draggable icon
        container.addEventListener("mousemove", trackMouse, {
            capture: true,
            signal: abortDrag.current.signal,
        });
        // only trigger endDrag once, this way we don't have to remove it manually
        container.addEventListener("mouseup", endDrag, { once: true });
        trackMouse(evt);
    }, [trackMouse]);
    const onClose = React.useCallback(() => {
        props.onClose(overlayId);
    }, [props.onClose, overlayId]);
    const toggleOpen = React.useCallback(() => {
        setOpen((old) => !old);
    }, []);
    const className = `instructions-overlay ${overlay?.options?.className ?? ""}`;
    const overlayComponentProps = {
        ...(overlay?.options?.props && typeof overlay.options.props === "function"
            ? overlay.options.props()
            : (overlay?.options?.props ?? {})),
        closeOverlay: () => props.onClose(overlayId),
    };
    return ReactDOM.createPortal(React.createElement("div", { className: className, ref: ref, style: { left: pos.x, top: pos.y, height: open ? undefined : "auto" } },
        React.createElement(FlexLayout, { type: "column" },
            React.createElement(FlexLayout.Fixed, null,
                React.createElement(FlexLayout, { className: "instructions-overlay-header", type: "row" },
                    React.createElement(FlexLayout.Flex, { className: "instructions-overlay-title", draggable: true, onDragStart: startDrag },
                        overlay?.options?.showIcon ? React.createElement(Icon, { name: "dialog-info" }) : null,
                        React.createElement("h4", null, t(overlay?.options?.containerTitle ?? "Instructions"))),
                    React.createElement(FlexLayout.Fixed, { className: "instructions-overlay-buttons" },
                        React.createElement(tooltip.IconButton, { className: "btn-embed", icon: open ? "collapse-overlay" : "expand-overlay", tooltip: open ? t("Collapse") : t("Expand"), onClick: toggleOpen }),
                        React.createElement(tooltip.IconButton, { className: "btn-embed", icon: "close", tooltip: t("Close"), onClick: onClose })))),
            React.createElement(FlexLayout.Fixed, { className: "instructions-overlay-mod-name" }, overlay.title ? React.createElement("h3", null, overlay.title) : null),
            React.createElement(FlexLayout.Fixed, { style: { overflowY: "auto" } }, open ? ((() => {
                if (overlay.componentId) {
                    // Handle component rendering
                    const registeredComponent = getOverlayComponent(overlay.componentId);
                    if (registeredComponent) {
                        let componentProps = overlayComponentProps;
                        if (overlay.options?.props) {
                            if (typeof overlay.options.props === "function") {
                                const additionalProps = overlay.options.props();
                                componentProps = {
                                    ...overlayComponentProps,
                                    ...additionalProps,
                                };
                            }
                            else {
                                componentProps = {
                                    ...overlayComponentProps,
                                    ...overlay.options.props,
                                };
                            }
                        }
                        return React.createElement(registeredComponent, componentProps);
                    }
                    else {
                        return (React.createElement("div", { className: "instructions-overlay-error" },
                            "Component not found for ID: ",
                            overlay.componentId));
                    }
                }
                if (overlay.content) {
                    // Handle text/markdown content
                    return (React.createElement(ReactMarkdown, { allowedElements: ["p", "br", "a", "em", "strong"], className: "instructions-overlay-content", unwrapDisallowed: true }, overlay.content));
                }
                return (React.createElement("div", { className: "instructions-overlay-error" }, "No content provided"));
            })()) : (React.createElement("div", { className: "instructions-overlay-error" }, "Overlay closed"))))), container);
}
export default InstructionsOverlay;
